import type { FinishModalFontSchemaType, FinishModalSchemaType } from '@vedalib/editor/lib/brand'
import { CONFIG_COURSE, CONFIG_ELEMENTS } from '@vedalib/editor/lib/elements'
import React, { useCallback, useMemo } from 'react'

import BaseButtonElement from 'components/Editor/EditorElements/elements/Button/BaseButtonElement'
import { useElementStyleCss, useElementFontCss } from 'services/Branding/hooks'
import type { ElementFontCss, ElementStyleCss } from 'services/Branding/types'
import { useProjectT } from 'services/ProjectLabels'
import type { IBlockMode } from 'services/Store/Project/types'

import CourseModal from '../../components/CourseModal'
import * as s from './FinishModal.module.scss'

interface ICourseFinishedModalProps {
  mode: IBlockMode
}

const MODAL_TITLE_ID = 'finishModalTitle'
const MODAL_DESCRIPTION_ID = 'finishModalDescription'

export const CourseFinishedModalContent: React.FC<{
  onClose: () => void
  mode: IBlockMode
  styles: ElementStyleCss<FinishModalSchemaType>
  fonts: ElementFontCss<FinishModalFontSchemaType>
}> = ({ onClose, mode, styles, fonts }) => {
  const pt = useProjectT()
  const buttonStyles = useElementStyleCss(CONFIG_ELEMENTS.button.type, mode.deviceMode)
  const buttonFonts = useElementFontCss(CONFIG_ELEMENTS.button.type, mode.deviceMode)
  return (
    <div className={s.root} style={styles.background}>
      <div className={s.title} id={MODAL_TITLE_ID} style={fonts?.h3}>
        {pt('finishModal.title')}
      </div>
      <div className={s.text} id={MODAL_DESCRIPTION_ID} style={fonts?.base}>
        {pt('finishModal.text')}
      </div>
      <div className={s.button}>
        <BaseButtonElement
          action='continueViewing'
          font={buttonFonts}
          label={pt('finishModal.button')}
          mode={mode}
          onClick={onClose}
          styles={buttonStyles}
        />
      </div>
    </div>
  )
}

const FinishModal: React.FC<ICourseFinishedModalProps> = ({ mode }) => {
  const [open, setOpen] = React.useState(true)
  const styles = useElementStyleCss(CONFIG_COURSE.finishModal.type, mode.deviceMode)
  const fonts = useElementFontCss(CONFIG_COURSE.finishModal.type, mode.deviceMode)
  const props = useMemo(() => ({ mode, styles, fonts }), [mode, styles, fonts])
  const onClose = useCallback(() => setOpen(false), [])

  if (!styles.value.show) {
    return null
  }

  return (
    <CourseModal
      ariaDescribedby={MODAL_DESCRIPTION_ID}
      ariaLabelledby={MODAL_TITLE_ID}
      component={CourseFinishedModalContent}
      maxWidth={640}
      mode={mode}
      onClose={onClose}
      props={props}
      visible={open}
    />
  )
}

export default FinishModal
